import { mediaQueryLarge, unlockScroll } from '@theme/utilities';
import { DialogOpenEvent } from '@theme/dialog';

/**
 * Slider carousel
 *
 * A thin enhancement around the core <slideshow-component> (assets/slideshow.js).
 * All scrolling, dragging, autoplay, arrows, dots, looping and theme editor
 * integration are handled by the core slideshow. Layout (slides per view, slide
 * width / peeking neighbours) is pure CSS, set per breakpoint by blocks/slider.liquid.
 * This element only adds "center mode" for an uneven number of visible slides:
 *
 * - the active slide is the one in the middle of the viewport (not the left-most one)
 * - next / previous move one slide at a time (instead of a full page)
 * - the centered slide and its neighbours get a position attribute for styling
 * - clicking a side slide brings it to the center
 *
 * The core class is not modified or re-implemented. Instead, a few of its public
 * getters are overridden on this one slideshow instance and delegate to the
 * original implementation whenever center mode is not active (even number of
 * slides at the current breakpoint, or center mode disabled).
 *
 * Optional "endless" mode (attribute [endless]):
 *
 * - slides continue seamlessly in both directions instead of rewinding at the end
 * - the DOM order is never touched (YouTube iframes would reload, and the core
 *   slideshow, dots and theme editor all rely on it). Instead the slides are
 *   re-arranged visually with CSS `order`, so the active slide always has
 *   slides on both sides. The scroll position is corrected in the same frame,
 *   so the re-arrangement is invisible.
 * - needs at least two more slides than are visible (peeking slides included),
 *   otherwise the slider falls back to the normal behavior.
 *
 * Video pop-up:
 *
 * - while several slides are visible, a click-to-play video (Horizon's <deferred-media>,
 *   e.g. the Video block) opens in a pop-up instead of playing inside its small slide.
 *   With one visible slide it keeps playing inline.
 * - uses the theme's own <dialog-component> (assets/dialog.js, as used by the Popup link
 *   block) and a fresh copy of the <deferred-media>, so media.js and video-youtube-short.js
 *   load and play it exactly as they do inline. Closing the pop-up removes the copy, which
 *   stops it; there is no separate stop logic.
 * - while the pop-up is in use, slide videos never play inside their slide (one that was
 *   started there, e.g. before a breakpoint change, is reset to its poster).
 * - the pop-up markup comes from a <template> in blocks/slider.liquid and is only added
 *   to the page on first use.
 *
 * Attributes:
 * - columns: number of slides visible on desktop
 * - columns-mobile: number of slides visible on mobile
 * - center-mode: breakpoints with center mode, e.g. "desktop mobile"
 * - peek: breakpoints where the neighbouring slides peek in, e.g. "desktop mobile"
 * - endless: enables endless mode
 */

const SLIDESHOW_TAG = 'slideshow-component';

/** Distance in px a pointer may move before a click is treated as a drag. */
const CLICK_TOLERANCE = 6;

/** Time in ms without scroll events after which a scroll counts as finished (endless mode). */
const SCROLL_IDLE = 160;

/** Share of a slide that has to be on screen for its video to open in the pop-up (otherwise it slides in first). */
const VIDEO_VISIBLE_RATIO = 0.9;

/**
 * Positive modulo.
 * @param {number} value
 * @param {number} length
 */
const mod = (value, length) => ((value % length) + length) % length;

/**
 * Returns a <deferred-media> (snippets/video.liquid) to its not yet loaded state: only the
 * poster, the template (and a Short's sound button) stay, so a click on the poster starts it again.
 * @param {HTMLElement} media
 */
const unloadVideo = (media) => {
  for (const child of media.querySelectorAll(':scope > :not(template, .deferred-media__poster-button, .video-short__sound)')) {
    child.remove();
  }
  for (const name of ['data-media-loaded', 'data-short-js', 'data-short-ready', 'data-short-buffering']) {
    media.removeAttribute(name);
  }
  media.classList.remove('is-playing');
  media.querySelector(':scope > .deferred-media__poster-button')?.classList.remove('deferred-media__playing');

  /** @type {any} */ (media).isPlaying = false;
};

class SliderCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #slideshow = null;

  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #frame = 0;

  /** @type {number} */
  #slideHeight = 0;

  /** @type {{ x: number, y: number } | null} */
  #pointer = null;

  /** @type {AbortController | null} */
  #controller = null;

  /**
   * Visual position (CSS order) per slide index while endless mode is active.
   * @type {number[] | null}
   */
  #order = null;

  /** @type {number} */
  #idleTimer = 0;

  /** Whether a finger is on the slider (endless mode never re-arranges then). */
  #touching = false;

  /** @type {'desktop' | 'mobile' | null} */
  #layoutBreakpoint = null;

  /** @type {(HTMLElement & { showDialog?: () => void, closeDialog?: () => void }) | null} */
  #videoModal = null;

  connectedCallback() {
    this.#slideshow = this.querySelector(`:scope > ${SLIDESHOW_TAG}`);
    this.#scroller = this.#slideshow?.querySelector(':scope > slideshow-container > slideshow-slides') ?? null;

    if (!this.#slideshow || !this.#scroller) return;

    this.#extendSlideshow(this.#slideshow);
    this.#layoutBreakpoint = this.#breakpoint;
    if (this.#videoModalActive) this.#resetSlideVideos();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#scroller.addEventListener('scroll', this.#requestUpdate, { passive: true, signal });
    this.#scroller.addEventListener('pointerdown', this.#handlePointerDown, { passive: true, signal });
    this.#scroller.addEventListener('click', this.#handleClick, { capture: true, signal });
    // Window capture runs before the theme's delegated on:click handlers (document capture),
    // so a video can be sent to the pop-up before it starts playing inside the slide
    window.addEventListener('click', this.#handleVideoClick, { capture: true, signal });

    // The resize event comes before the scroll caused by the new layout; the others are fallbacks
    window.addEventListener('resize', this.#handleBreakpointChange, { passive: true, signal });
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange, { signal });

    if (this.hasAttribute('endless')) {
      this.#scroller.addEventListener('scroll', this.#scheduleRebalance, { passive: true, signal });
      this.#scroller.addEventListener('touchstart', this.#handleTouchStart, { passive: true, signal });
      this.#scroller.addEventListener('touchend', this.#handleTouchEnd, { passive: true, signal });
      this.#scroller.addEventListener('touchcancel', this.#handleTouchEnd, { passive: true, signal });

      // Arrange the slides right away, so the first paint already shows the final layout
      this.#rebalance(this.#initialIndex, { compensate: false });
      this.#alignTo(this.slides[this.#initialIndex]);
    }

    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this.#scroller);

    customElements.whenDefined(SLIDESHOW_TAG).then(() => {
      // Let the core slideshow finish its own setup first
      requestAnimationFrame(() => requestAnimationFrame(() => this.#scrollToInitialSlide()));
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    clearTimeout(this.#idleTimer);
    this.#idleTimer = 0;
  }

  /**
   * The current breakpoint, as used in the [center-mode] and [peek] attributes.
   * @returns {'desktop' | 'mobile'}
   */
  get #breakpoint() {
    return mediaQueryLarge.matches ? 'desktop' : 'mobile';
  }

  /**
   * Whether a breakpoint list attribute contains the current breakpoint.
   * @param {string} name
   * @returns {boolean}
   */
  #activeHere(name) {
    return (this.getAttribute(name) ?? '').split(/\s+/).includes(this.#breakpoint);
  }

  /**
   * Whether center mode is currently active.
   * @returns {boolean}
   */
  get centerMode() {
    return this.#activeHere('center-mode');
  }

  /**
   * Number of slides visible at the current breakpoint.
   * @returns {number}
   */
  get columns() {
    const attribute = mediaQueryLarge.matches ? 'columns' : 'columns-mobile';
    return parseInt(this.getAttribute(attribute) ?? '1', 10) || 1;
  }

  /**
   * The slides handled by the slideshow.
   * @returns {HTMLElement[]}
   */
  get slides() {
    const slideshow = /** @type {any} */ (this.#slideshow);
    const slides = slideshow?.slides;
    if (Array.isArray(slides)) return slides;

    return /** @type {HTMLElement[]} */ (Array.from(this.#scroller?.querySelectorAll(':scope > slideshow-slide') ?? []));
  }

  /**
   * Number of neighbouring slides that peek in: on both sides in centered
   * layouts, otherwise only after the visible slides.
   * @returns {number}
   */
  get #peekCount() {
    if (!this.#activeHere('peek')) return 0;
    return this.#centered ? 2 : 1;
  }

  /**
   * Number of slides on screen at once, including the peeking ones.
   * @returns {number}
   */
  get #visibleCount() {
    return this.columns + this.#peekCount;
  }

  /**
   * Slides on screen in front of the active slide (including a peeking one): the
   * active slide is the centered one in center mode, otherwise the first one.
   * @returns {number}
   */
  get #lead() {
    const peekBefore = this.#peekCount === 2 ? 1 : 0;
    return (this.centerMode ? Math.floor(this.columns / 2) : 0) + peekBefore;
  }

  /**
   * Whether the active slide is snapped to the center (a single slide or center mode)
   * rather than to the start. Matches --slider-centered in blocks/slider.liquid.
   * @returns {boolean}
   */
  get #centered() {
    return this.centerMode || this.columns === 1;
  }

  /**
   * Whether endless mode is currently active.
   * @returns {boolean}
   */
  get endless() {
    if (!this.hasAttribute('endless')) return false;

    // Needs a spare slide on both sides of what is visible
    return this.slides.length >= this.#visibleCount + 2;
  }

  /**
   * @returns {number}
   */
  get #initialIndex() {
    const index = parseInt(this.#slideshow?.getAttribute('initial-slide') ?? '0', 10) || 0;
    return mod(index, this.slides.length || 1);
  }

  /**
   * Overrides a few public getters/methods of this slideshow instance so the core
   * logic works with a centered active slide. Everything falls back to the
   * original implementation when center mode is not active.
   *
   * Own properties survive the custom element upgrade, so this works no matter
   * whether slideshow.js has already been defined or not.
   *
   * @param {HTMLElement} slideshow
   */
  #extendSlideshow(slideshow) {
    const host = this;
    let bypass = false;
    let endlessSelect = false;

    /**
     * @param {string} name
     * @returns {PropertyDescriptor | undefined}
     */
    const original = (name) => {
      const Slideshow = customElements.get(SLIDESHOW_TAG);
      return Slideshow ? Object.getOwnPropertyDescriptor(Slideshow.prototype, name) : undefined;
    };

    Object.defineProperties(slideshow, {
      /**
       * The core uses the visible slides to find the current slide and the step size.
       * In center mode only the slide closest to the center counts as "visible",
       * which makes the core track the centered slide and step one slide at a time.
       */
      visibleSlides: {
        configurable: true,
        get() {
          // Endless mode: the target is always adjacent visually, so the core must not use
          // its single-slide DOM reorder trick (it only runs when exactly one slide is visible)
          if (endlessSelect) return [];

          /** @type {HTMLElement[]} */
          const visible = original('visibleSlides')?.get?.call(this) ?? [];
          if (bypass || !host.centerMode || visible.length <= 1) return visible;

          const centered = host.#closestToCenter(visible);
          return centered ? [centered] : visible;
        },
      },

      nextIndex: {
        configurable: true,
        get() {
          if (host.centerMode) return this.current + 1;
          return original('nextIndex')?.get?.call(this);
        },
      },

      previousIndex: {
        configurable: true,
        get() {
          if (host.centerMode) return this.current - 1;
          return original('previousIndex')?.get?.call(this);
        },
      },

      /**
       * With several slides on screen, the core should scroll directly to the
       * target instead of using its single-slide reorder trick for long jumps.
       * The real visible slides are exposed for the synchronous part of select().
       */
      select: {
        configurable: true,
        writable: true,
        value(/** @type {any[]} */ ...args) {
          const select = original('select')?.value;
          if (typeof select !== 'function') return;

          if (host.endless) {
            const [input, ...rest] = args;
            const target = host.#prepareEndlessSelect(input, this.current);

            if (target !== undefined) {
              endlessSelect = true;
              try {
                // Everything up to the first await in the core select() runs synchronously
                return select.call(this, target, ...rest);
              } finally {
                endlessSelect = false;
              }
            }
          }

          bypass = host.centerMode;
          try {
            return select.apply(this, args);
          } finally {
            bypass = false;
          }
        },
      },
    });
  }

  /**
   * The slide that is currently active visually: the centered one, or in start-aligned
   * layouts the one closest to the start edge.
   * @returns {HTMLElement | undefined}
   */
  #activeSlide() {
    if (this.#centered) return this.#closestToCenter();
    if (!this.#scroller) return undefined;

    const start = this.#scroller.getBoundingClientRect().left;
    let closest;
    let smallestDistance = Infinity;

    for (const slide of this.slides) {
      const distance = Math.abs(slide.getBoundingClientRect().left - start);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        closest = slide;
      }
    }

    return closest;
  }

  /**
   * Re-arranges the slides visually (CSS order) so the anchor slide has room on both sides,
   * and corrects the scroll position so nothing moves on screen.
   *
   * @param {number} [anchorIndex] - Slide to arrange around (defaults to the active slide)
   * @param {{ position?: number, compensate?: boolean }} [options]
   *   position: visual position of the anchor slide, compensate: keep the view in place
   */
  #rebalance(anchorIndex, { position, compensate = true } = {}) {
    const { slides } = this;
    const scroller = this.#scroller;
    if (!scroller) return;

    if (!this.endless) {
      if (this.#order) {
        for (const slide of slides) slide.style.removeProperty('order');
        this.#order = null;

        // Back to the DOM order (e.g. too few slides for this breakpoint): keep the current slide in view
        const slideshow = /** @type {any} */ (this.#slideshow);
        this.#alignTo(slides[slideshow?.current ?? 0]);
      }
      return;
    }

    const length = slides.length;
    const reference = this.#activeSlide();
    const anchor = anchorIndex ?? (reference ? slides.indexOf(reference) : -1);
    if (anchor < 0) return;

    const defaultPosition = Math.floor((length - this.#visibleCount) / 2) + this.#lead;
    const anchorPosition = position ?? defaultPosition;
    const order = slides.map((_, index) => mod(index - anchor + anchorPosition, length));

    if (this.#order && order.every((value, index) => value === this.#order?.[index])) return;

    const before = compensate && reference ? reference.getBoundingClientRect().left : 0;

    slides.forEach((slide, index) => slide.style.setProperty('order', String(order[index])));
    this.#order = order;

    if (compensate && reference) {
      const delta = reference.getBoundingClientRect().left - before;
      if (Math.abs(delta) > 0.5) scroller.scrollTo({ left: scroller.scrollLeft + delta, behavior: 'instant' });
    }

    this.#requestUpdate();
  }

  /**
   * Resolves a select() input to a slide index and arranges the slides so the
   * target can be reached by scrolling in the intended direction.
   *
   * Numbers (arrows, dots, autoplay) keep their direction: below the current index
   * goes backward, above goes forward, also when they are out of range (wrapping).
   * Slide ids take the shortest way.
   *
   * @param {number | string | { id: string } | undefined} input
   * @param {number} current - The core's current index
   * @returns {number | undefined} The normalized target index
   */
  #prepareEndlessSelect(input, current) {
    const { slides } = this;
    const length = slides.length;

    let raw;
    if (typeof input === 'number') raw = input;
    else if (typeof input === 'string') raw = parseInt(input, 10);
    else if (input && typeof input === 'object' && 'id' in input) {
      raw = slides.findIndex((slide) => slide.getAttribute('slide-id') == input.id);
      if (raw === -1) return undefined;
    }
    if (raw === undefined || Number.isNaN(raw)) return undefined;

    const target = mod(raw, length);
    const reference = this.#activeSlide();
    const anchor = reference ? slides.indexOf(reference) : current;
    if (target === anchor) return target;

    let direction;
    if (typeof input === 'object') {
      direction = mod(target - anchor, length) <= length / 2 ? 1 : -1;
    } else {
      direction = raw >= current ? 1 : -1;
    }

    const steps = direction > 0 ? mod(target - anchor, length) : mod(anchor - target, length);
    const visible = this.#visibleCount;
    const lead = this.#lead;
    const defaultPosition = Math.floor((length - visible) / 2) + lead;
    const min = lead;
    const max = length - visible + lead;

    // Room needed after (forward) or before (backward) the anchor so the target ends up in place
    const position =
      direction > 0
        ? Math.max(min, Math.min(defaultPosition, max - steps))
        : Math.min(max, Math.max(defaultPosition, min + steps));

    this.#rebalance(anchor, { position });

    return target;
  }

  /**
   * Scrolls a slide into its resting position without animation.
   * @param {HTMLElement | undefined} slide
   */
  #alignTo(slide) {
    const scroller = this.#scroller;
    if (!slide || !scroller) return;

    const paddingStart = parseFloat(getComputedStyle(scroller).paddingInlineStart) || 0;
    scroller.scrollTo({ left: slide.offsetLeft - paddingStart, behavior: 'instant' });
  }

  /**
   * Re-arranges the slides once scrolling has come to rest.
   */
  #scheduleRebalance = () => {
    clearTimeout(this.#idleTimer);
    this.#idleTimer = window.setTimeout(() => {
      this.#idleTimer = 0;

      // Never while a finger or the mouse is still dragging the slider
      if (this.#touching || this.#slideshow?.hasAttribute('dragging')) {
        this.#scheduleRebalance();
        return;
      }

      this.#rebalance();
    }, SCROLL_IDLE);
  };

  #handleTouchStart = () => {
    this.#touching = true;
  };

  #handleTouchEnd = () => {
    this.#touching = false;
    this.#scheduleRebalance();
  };

  /**
   * Returns the slide whose center is closest to the center of the scroller.
   * @param {HTMLElement[]} [slides]
   * @returns {HTMLElement | undefined}
   */
  #closestToCenter(slides = this.slides) {
    if (!this.#scroller || !slides.length) return undefined;

    const scrollerRect = this.#scroller.getBoundingClientRect();
    const center = scrollerRect.left + scrollerRect.width / 2;

    let closest;
    let smallestDistance = Infinity;

    for (const slide of slides) {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        closest = slide;
      }
    }

    return closest;
  }

  /**
   * The core slideshow sets its current index to [initial-slide] but does not
   * scroll there on load, so align the initial slide once everything is laid out.
   */
  #scrollToInitialSlide() {
    const slideshow = /** @type {any} */ (this.#slideshow);
    const scroller = this.#scroller;
    if (!slideshow || !scroller) return;

    const index = parseInt(slideshow.getAttribute('initial-slide') ?? '0', 10) || 0;
    const slide = this.slides[index];

    if (this.endless) {
      this.#rebalance(index, { compensate: false });
      this.#alignTo(slide);
    } else if (index > 0 && slide) {
      const paddingStart = parseFloat(getComputedStyle(scroller).paddingInlineStart) || 0;
      scroller.scrollTo({ left: slide.offsetLeft - paddingStart, behavior: 'instant' });
    }

    this.#update();
  }

  #handleResize = () => {
    this.#handleBreakpointChange();
    this.#requestUpdate();
  };

  /**
   * Columns, center mode and peeking can differ per breakpoint, so the browser's own
   * re-snapping after the layout change may land on a different slide (especially while
   * the center slide is still scaling down). Keep the active slide in place instead.
   *
   * Runs on window resize, i.e. after the new layout but before the scroll caused by it
   * is reported, so the core's current index is still the previous slide.
   */
  #handleBreakpointChange = () => {
    const breakpoint = this.#breakpoint;
    if (breakpoint === this.#layoutBreakpoint) return;
    this.#layoutBreakpoint = breakpoint;

    const slideshow = /** @type {any} */ (this.#slideshow);
    const index = typeof slideshow?.current === 'number' ? slideshow.current : this.#initialIndex;
    const slide = this.slides[index];
    if (!slide) return;

    if (this.hasAttribute('endless')) this.#rebalance(index, { compensate: false });
    this.#alignTo(slide);
    this.#requestUpdate();

    if (this.#videoModalActive) this.#resetSlideVideos();
  };

  #requestUpdate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(this.#update);
  };

  /**
   * Marks the centered slide and its neighbours and measures the slide height,
   * which is used to reserve room for the enlarged center slide.
   */
  #update = () => {
    this.#frame = 0;

    const { slides } = this;
    if (!slides.length) return;

    const tallest = Math.max(...slides.map((slide) => slide.offsetHeight));
    if (tallest > 0 && tallest !== this.#slideHeight) {
      this.#slideHeight = tallest;
      this.style.setProperty('--slider-slide-height', `${tallest}px`);
    }

    if (!this.centerMode) {
      for (const slide of slides) slide.removeAttribute('slider-position');
      return;
    }

    const centered = this.#closestToCenter(slides);
    const centerIndex = centered ? slides.indexOf(centered) : -1;
    const ring = Math.floor(this.columns / 2);

    // Keep the core index in sync with the slide that is visually centered.
    // The core only updates it from its own (asynchronous) visibility data,
    // which can lag behind after the initial scroll.
    const slideshow = /** @type {any} */ (this.#slideshow);
    if (slideshow && centerIndex > -1 && slideshow.current !== centerIndex && !slideshow.hasAttribute('transitioning')) {
      slideshow.current = centerIndex;
    }

    const order = this.#order;

    slides.forEach((slide, index) => {
      // In endless mode neighbours are defined by the visual order, not the index
      const distance = order ? Math.abs(order[index] - order[centerIndex]) : Math.abs(index - centerIndex);

      if (distance === 0) {
        slide.setAttribute('slider-position', 'center');
      } else if (distance < ring) {
        slide.setAttribute('slider-position', 'near');
      } else {
        slide.removeAttribute('slider-position');
      }
    });
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    this.#pointer = { x: event.clientX, y: event.clientY };
  };

  /**
   * Clicking a slide that is not the active one moves it into place
   * instead of following links inside of it.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const slideshow = /** @type {any} */ (this.#slideshow);
    if (!slideshow || !(event.target instanceof Element)) return;

    if (this.#pointer) {
      const moved = Math.hypot(event.clientX - this.#pointer.x, event.clientY - this.#pointer.y);
      this.#pointer = null;
      if (moved > CLICK_TOLERANCE) return;
    }

    // Find the slide that belongs to this slider (ignore nested slideshows)
    const slide = this.#slideOf(event.target);
    if (!slide) return;

    const index = this.slides.indexOf(slide);
    if (index === -1) return;

    // The visually centered slide is the active one, even if the core index lags behind
    if (this.centerMode && slide === this.#closestToCenter()) {
      if (slideshow.current !== index) slideshow.current = index;
      return;
    }

    if (index === slideshow.current) return;

    // Outside of center mode, only react to slides that are not fully visible
    if (!this.centerMode && slide.getAttribute('aria-hidden') !== 'true') return;

    event.preventDefault();
    event.stopPropagation();

    this.#selectSlide(slide, event);
  };

  /**
   * Moves a slide into place.
   * @param {HTMLElement} slide
   * @param {Event} [event]
   */
  #selectSlide(slide, event) {
    const slideshow = /** @type {any} */ (this.#slideshow);
    const index = this.slides.indexOf(slide);
    if (!slideshow || index === -1) return;

    if (this.endless) {
      // Select by id so the slider takes the shortest (visual) way to the clicked slide
      const id = slide.getAttribute('slide-id');
      if (id) {
        slideshow.select({ id }, event);
        return;
      }
    }

    slideshow.select(index, event);
  }

  /**
   * The slide of this slider (not of a nested slideshow) that contains an element.
   * @param {Element} element
   * @returns {HTMLElement | null}
   */
  #slideOf(element) {
    let slide = element.closest('slideshow-slide');
    while (slide && slide.parentElement !== this.#scroller) {
      slide = slide.parentElement?.closest('slideshow-slide') ?? null;
    }
    return slide instanceof HTMLElement ? slide : null;
  }

  /**
   * Share of a slide's width that is inside the slider.
   * @param {HTMLElement} slide
   * @returns {number}
   */
  #visibleRatio(slide) {
    if (!this.#scroller) return 0;

    const rect = slide.getBoundingClientRect();
    const bounds = this.#scroller.getBoundingClientRect();
    const visible = Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left);

    return rect.width > 0 ? Math.max(0, visible) / rect.width : 0;
  }

  /**
   * Sends click-to-play videos to the pop-up while several slides are visible.
   * @param {MouseEvent} event
   */
  #handleVideoClick = (event) => {
    if (!this.#videoModalActive || !(event.target instanceof Element)) return;

    const button = event.target.closest('deferred-media > .deferred-media__poster-button');
    const media = button?.parentElement;
    if (!(button instanceof HTMLElement) || !media) return;

    // Autoplaying videos stay in the slide, and so do videos of nested sliders
    if (media.hasAttribute('autoplay') || media.closest('slider-carousel') !== this) return;

    const slide = this.#slideOf(media);
    if (!slide) return;

    // Every click that reaches the poster is a real click: after a mouse drag the slideshow
    // captures the pointer (the click lands on it instead), and touch scrolling fires no click.
    // So the video must never start inside the slide from here on.
    this.#pointer = null;
    event.preventDefault();
    event.stopImmediatePropagation();

    // A slide that only peeks in slides into view first
    if (this.#visibleRatio(slide) < VIDEO_VISIBLE_RATIO) {
      this.#selectSlide(slide, event);
      return;
    }

    this.#openVideoModal(media, button);
  };

  /**
   * The pop-up, created from the template in blocks/slider.liquid on first use.
   * @returns {(HTMLElement & { showDialog?: () => void, closeDialog?: () => void }) | null}
   */
  #ensureVideoModal() {
    if (this.#videoModal?.isConnected) return this.#videoModal;

    const template = this.querySelector(':scope > template[data-slider-video-modal]');
    if (!(template instanceof HTMLTemplateElement)) return null;

    const modal = document.importNode(template.content, true).firstElementChild;
    if (!(modal instanceof HTMLElement)) return null;

    this.append(modal);

    // Every way of closing ends here (button, Escape, backdrop, or the browser closing it natively
    // when Escape is pressed inside the YouTube frame). Removing the copy stops the video; the
    // unlock only matters for the native close, which bypasses dialog-component.
    const dialog = modal.querySelector('dialog');
    dialog?.addEventListener('close', () => {
      modal.querySelector('[data-slider-video-media]')?.replaceChildren();
      unlockScroll(dialog);
    });

    this.#videoModal = /** @type {any} */ (modal);
    return this.#videoModal;
  }

  /**
   * Plays a copy of a slide's video in the pop-up.
   * Runs synchronously inside the click, so the browser allows playback with sound.
   * @param {HTMLElement} media - The <deferred-media> of the slide
   * @param {HTMLElement} button - Its poster button
   */
  #openVideoModal(media, button) {
    const modal = this.#ensureVideoModal();
    const holder = modal?.querySelector('[data-slider-video-media]');
    const copy = /** @type {any} */ (this.#copyVideo(media));

    // Without the pop-up markup (or before media.js is ready) fall back to playing inline
    if (!modal || !holder || typeof copy.showDeferredMedia !== 'function' || typeof modal.showDialog !== 'function') {
      /** @type {any} */ (media).showDeferredMedia?.();
      return;
    }

    modal.style.setProperty('--slider-video-ratio', String(this.#videoRatio(media, button)));
    // The slide's poster is already loaded: shown behind the copy, so the pop-up never opens black
    const poster = button.querySelector('img')?.currentSrc;
    modal.style.setProperty('--slider-video-poster', poster ? `url(${JSON.stringify(poster)})` : 'none');
    holder.replaceChildren(copy);

    // Start = the same as a click on the poster: media.js loads, plays and announces the video.
    // Uploaded videos start right away, inside the click (Safari only allows sound there).
    // YouTube / Vimeo frames start once the pop-up is visible: the dialog only opens on the next
    // frame, and a player that starts up hidden does not autoplay.
    const template = copy.querySelector(':scope > template');
    const isFrame = template instanceof HTMLTemplateElement && Boolean(template.content.querySelector('iframe'));

    if (isFrame && !modal.querySelector('dialog')?.open) {
      modal.addEventListener(DialogOpenEvent.eventName, () => this.#startFrame(copy), { once: true });
    } else {
      copy.showDeferredMedia();
    }

    modal.showDialog();
  }

  /**
   * Loads a YouTube / Vimeo copy in the open pop-up. The embed URL asks for autoplay; for
   * Shorts, video-youtube-short.js also shows a spinner while loading and starts the player
   * once it is ready.
   * @param {any} copy - The <deferred-media> copy in the pop-up
   */
  #startFrame(copy) {
    if (!copy.isConnected) return;

    // media.js moves the focus into the new frame, where Escape no longer reaches the
    // pop-up; keep it where the dialog put it (the close button)
    const focused = document.activeElement;
    copy.showDeferredMedia();
    if (focused instanceof HTMLElement && copy.contains(document.activeElement)) focused.focus({ preventScroll: true });
  }

  /**
   * A fresh, not yet loaded copy of a <deferred-media>, without the sizing of the slide.
   * @param {HTMLElement} media
   * @returns {HTMLElement}
   */
  #copyVideo(media) {
    const copy = /** @type {HTMLElement} */ (media.cloneNode(true));

    for (const { name } of [...copy.attributes]) {
      if (name === 'style' || name.startsWith('data-shopify')) copy.removeAttribute(name);
    }
    copy.classList.remove('spacing-style', 'size-style', 'border-style');
    unloadVideo(copy);
    for (const image of copy.querySelectorAll('img')) image.loading = 'eager';

    // Watching in the pop-up is a deliberate choice, so uploaded videos play with sound
    // (Horizon starts them muted; YouTube frames already do)
    const template = copy.querySelector(':scope > template');
    if (template instanceof HTMLTemplateElement) {
      for (const video of template.content.querySelectorAll('video')) video.removeAttribute('muted');
    }

    return copy;
  }

  /**
   * Whether slide videos open in the pop-up at the current breakpoint.
   * @returns {boolean}
   */
  get #videoModalActive() {
    return this.columns > 1;
  }

  /**
   * Puts slide videos that were started inside their slide back to their poster, so the
   * next click reaches the poster (and the pop-up) instead of the small player.
   */
  #resetSlideVideos() {
    for (const media of this.#scroller?.querySelectorAll('deferred-media[data-media-loaded]:not([autoplay])') ?? []) {
      if (media.closest('slider-carousel') !== this) continue;

      /** @type {any} */ (media).pauseMedia?.();
      unloadVideo(/** @type {HTMLElement} */ (media));
    }
  }

  /**
   * Width / height of a video, read from the markup of snippets/video.liquid.
   * @param {HTMLElement} media
   * @param {HTMLElement} button
   * @returns {number}
   */
  #videoRatio(media, button) {
    /** @param {string} value */
    const parse = (value) => {
      const [width, height] = value.split('/').map((part) => parseFloat(part));
      const ratio = height ? width / height : width;
      return Number.isFinite(ratio) && ratio > 0 ? ratio : 0;
    };

    return (
      parse(button.style.getPropertyValue('--video-aspect-ratio')) ||
      parse(media.style.getPropertyValue('--size-style-aspect-ratio')) ||
      (media.classList.contains('video-block--short') ? 9 / 16 : 16 / 9)
    );
  }
}

if (!customElements.get('slider-carousel')) {
  customElements.define('slider-carousel', SliderCarousel);
}